import { useEffect, useState } from "react";
import { Printer, Loader2, Download, ExternalLink, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { api, errMsg } from "../lib/api";

const OPTS = [
  ["include_statement", "Kontoauszug-PDF einfügen"],
  ["placeholders", "Platzhalterseiten „BELEG FEHLT“ für offene Buchungen"],
  ["stamps", "Stempelzeile auf Belegseiten (Auszug · Nr. · Datum · Betrag · Händler)"],
];

const PrintDialog = ({ open, onOpenChange, scope, label }) => {
  const [o, setO] = useState({ include_statement: true, placeholders: true, stamps: true, no_receipt_mode: "list" });
  const [job, setJob] = useState(null);
  useEffect(() => { if (open) setJob(null); }, [open]);
  useEffect(() => {
    if (job?.status !== "running") return;
    const t = setInterval(async () => setJob(await api.getPrint(job.id)), 1000);
    return () => clearInterval(t);
  }, [job?.id, job?.status]);
  const start = async () => {
    try { setJob(await api.startPrint({ scope: scope.statement_id ? "statement" : "year", ...scope, ...o })); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const p = job?.progress || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5" />Druckpaket – {label}</DialogTitle>
          <DialogDescription>Übersicht → Kontoauszug → Belege in Buchungsreihenfolge (A4).</DialogDescription>
        </DialogHeader>
        {!job && (
          <div className="grid gap-3">
            {OPTS.map(([k, l]) => (
              <label key={k} className="flex items-start gap-2.5 text-sm text-slate-800">
                <Checkbox checked={o[k]} onCheckedChange={(v) => setO({ ...o, [k]: !!v })} data-testid={`print-opt-${k}`} className="mt-0.5" />{l}
              </label>
            ))}
            <div className="grid gap-1 text-sm">
              <span className="font-medium text-slate-800">Buchungen „Kein Beleg nötig“</span>
              <select value={o.no_receipt_mode} onChange={(e) => setO({ ...o, no_receipt_mode: e.target.value })} data-testid="print-opt-no-receipt-mode" className="h-10 rounded-md border border-input bg-white px-2">
                <option value="list">nur in der Übersicht auflisten</option>
                <option value="page">zusätzlich eigene Platzhalterseite</option>
              </select>
            </div>
          </div>
        )}
        {job && (
          <div className="grid gap-2" data-testid="print-progress">
            {job.status === "running" && (
              <>
                <Progress value={p.total ? (p.done / p.total) * 100 : 5} className="h-2" />
                <div className="text-sm text-slate-600">{p.phase} … <span className="font-num">{p.done}/{p.total}</span></div>
              </>
            )}
            {job.status === "failed" && <div className="text-sm text-red-700" data-testid="print-error">Fehler: {job.error}</div>}
            {job.status === "done" && (
              <div className="grid gap-3">
                <div className="text-sm font-medium text-emerald-800" data-testid="print-done">Fertig – <span className="font-num">{job.page_count}</span> Seiten.</div>
                <div className="flex flex-wrap gap-2">
                  <Button asChild className="gap-1.5 bg-slate-900 hover:bg-slate-800"><a href={api.printFileUrl(job.id, true)} data-testid="print-download-btn"><Download className="h-4 w-4" />Herunterladen</a></Button>
                  <Button asChild variant="outline" className="gap-1.5"><a href={api.printFileUrl(job.id)} target="_blank" rel="noreferrer" data-testid="print-open-btn"><ExternalLink className="h-4 w-4" />Druckansicht öffnen</a></Button>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
          {!job && <Button onClick={start} data-testid="print-start-btn" className="gap-1.5 bg-slate-900 hover:bg-slate-800"><Printer className="h-4 w-4" />Erstellen</Button>}
          {job?.status === "running" && <Button disabled className="gap-1.5"><Loader2 className="h-4 w-4 animate-spin" />Wird erstellt …</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PrintButtons = ({ scope, label, testPrefix }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-print`}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`${testPrefix}-print-btn`} className="gap-1.5"><Printer className="h-3.5 w-3.5" />Druckpaket</Button>
      <Button variant="outline" size="sm" asChild className="gap-1.5">
        <a href={api.openListUrl(scope)} target="_blank" rel="noreferrer" data-testid={`${testPrefix}-open-list-btn`}><ListChecks className="h-3.5 w-3.5" />Nur offene als Liste drucken</a>
      </Button>
      <PrintDialog open={open} onOpenChange={setOpen} scope={scope} label={label} />
    </div>
  );
};
